import React, { useState } from "react";
import { Zap, ShoppingBag, X as XIcon } from "lucide-react";

/**
 * StickyBuyBar — mobile-only fixed bottom bar on the PDP.
 * Tapping "Buy Now" opens a bottom sheet that lets the shopper adjust qty and
 * (if the product has variants) pick the size — then complete a single-tap
 * checkout. On desktop it renders nothing (existing large buttons remain).
 */
export default function StickyBuyBar({
  price,
  mrp,
  unitLabel,
  variants,
  activeVariantId,
  onSelectVariant,
  qty,
  onQtyChange,
  outOfStock,
  onBuyNow,
  onAddToCart,
}) {
  const [open, setOpen] = useState(false);
  const savings = mrp > price ? Math.round(mrp - price) : 0;
  const needsVariant = Array.isArray(variants) && variants.length > 0 && !activeVariantId;

  const doBuy = () => {
    if (needsVariant) { setOpen(true); return; }
    onBuyNow?.();
  };

  return (
    <>
      {/* Sticky bar — mobile only. Sits ABOVE MobileBottomNav (which is ~56px tall / bottom-0). */}
      <div
        className="md:hidden fixed bottom-14 left-0 right-0 z-[60] bg-white border-t hairline shadow-[0_-6px_24px_rgba(0,0,0,0.08)]"
        data-testid="sticky-buy-bar"
      >
        <div className="flex items-stretch gap-2 px-3 py-2.5">
          <div className="flex flex-col justify-center flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-xl gold-text" data-testid="sticky-price">₹{price}</span>
              {mrp > price && <span className="text-xs text-muted-foreground line-through">₹{mrp}</span>}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">
              {unitLabel || (savings > 0 ? `Save ₹${savings}` : "Handcrafted")}
            </div>
          </div>
          <button
            onClick={() => onAddToCart?.()}
            disabled={outOfStock}
            className={`px-4 rounded-full text-[11px] uppercase tracking-widest flex items-center gap-1.5 ${outOfStock ? "bg-muted text-muted-foreground" : "btn-outline-gold"}`}
            data-testid="sticky-add-cart"
          >
            <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Cart
          </button>
          <button
            onClick={doBuy}
            disabled={outOfStock}
            className={`px-5 rounded-full text-[11px] uppercase tracking-widest flex items-center gap-1.5 ${outOfStock ? "bg-muted text-muted-foreground" : "btn-gold"}`}
            data-testid="sticky-buy-now"
          >
            <Zap className="w-4 h-4" strokeWidth={1.5} /> Buy Now
          </button>
        </div>
      </div>

      {/* Variant-select bottom sheet */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-[60] bg-black/60"
          onClick={() => setOpen(false)}
          data-testid="sticky-sheet-backdrop"
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-5 pb-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="sticky-sheet"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-serif text-xl">Choose Size</div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full border hairline flex items-center justify-center"
                data-testid="sticky-sheet-close"
                aria-label="Close"
              >
                <XIcon className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {(variants || []).map((v) => {
                const oos = (v.stock || 0) <= 0;
                const active = v.variant_id === activeVariantId;
                return (
                  <button
                    key={v.variant_id}
                    onClick={() => !oos && onSelectVariant?.(v.variant_id)}
                    disabled={oos}
                    className={`px-4 py-2 rounded-full text-sm border transition ${active ? "bg-[#B8860B] text-ivory border-[#B8860B]" : "bg-white hairline hover:border-[#B8860B]"} ${oos ? "opacity-50 line-through" : ""}`}
                    data-testid={`sheet-variant-${(v.size || "").replace(/\s+/g, "-")}`}
                  >
                    <span className="font-medium">{v.size}</span>
                    <span className={`ml-2 text-xs ${active ? "opacity-90" : "text-muted-foreground"}`}>₹{v.price}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Qty</div>
              <div className="flex items-center border hairline rounded-full">
                <button onClick={() => onQtyChange?.(Math.max(1, qty - 1))} className="px-3 py-1.5" data-testid="sheet-qty-minus">−</button>
                <div className="px-4 text-sm">{qty}</div>
                <button onClick={() => onQtyChange?.(qty + 1)} className="px-3 py-1.5" data-testid="sheet-qty-plus">+</button>
              </div>
            </div>

            <button
              onClick={() => { setOpen(false); onBuyNow?.(); }}
              disabled={outOfStock || (variants && variants.length > 0 && !activeVariantId)}
              className={`w-full rounded-full py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2 ${(!activeVariantId && variants?.length) || outOfStock ? "bg-muted text-muted-foreground cursor-not-allowed" : "btn-gold"}`}
              data-testid="sheet-buy-now"
            >
              <Zap className="w-4 h-4" strokeWidth={1.5} />
              {outOfStock ? "Out of Stock" : "Buy Now"}
            </button>
            <button
              onClick={() => { setOpen(false); onAddToCart?.(); }}
              disabled={outOfStock || (variants && variants.length > 0 && !activeVariantId)}
              className="w-full mt-2 rounded-full py-3 text-xs uppercase tracking-widest btn-outline-gold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="sheet-add-cart"
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Add to Cart
            </button>
          </div>
        </div>
      )}
    </>
  );
}
